/**
 * `VehicleDetailsManual` provides a modal wizard for manually selecting vehicle
 * details when automated VRN/VIN lookup isn't used or fails.
 *
 * Key behaviour:
 * - Presents a cascading set of dropdowns (type -> make -> model -> fuel type ->
 *   engine size -> model trim) backed by `VehicleApi` lookups.
 * - Enables the confirm button only when all required selections are made
 *   (and first registration date unless optional).
 * - Supports non-GB journeys with auto-opening behaviour and optional
 *   prepopulation (e.g. from VIN warranty expiry details / brand rules).
 * - Translates vehicle type/fuel type labels when mapping lookup results.
 */
import React, { useEffect, useState, useRef } from 'react';
//@ts-ignore
import DateInput from '../DateInput/DateInput';
//@ts-ignore
import './VehiclePage.scss';
import { ClientDateHandler } from '../ClientDateHandler/ClientDateHandler';
import {
  SelectOpt,
  ManualQuerySelections,
  VehicleInfoLookup,
  VehicleModelTrimLookup,
  VehicleState,
  VinWarrantyExpiry
} from '../../common/CommonTypes';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { PageAbsolutePath, VehicleLookup } from '../../../consts/Common';
import { VehicleApi } from '../../../api/VehicleApi';
import { getDateString } from '../../common/HelperMethods';
import { ButtonConfirm } from '../Button/ButtonConfirm';
import { ButtonIcon } from '../Button/ButtonIcon';
import { ButtonCommand } from '../Button/ButtonCommand';
import { CommonValidation } from '../CommonValidation/commonValidation';
import SelectInput, { SelectInputHelper } from '../SelectInput/SelectInput';
import { CountryIsoCodes } from '../Constants';
import { useAppSelector } from '../../../app-state/Hooks';
import { useCurrentPath } from '../../sitePages/useCurrentPath';
import { useTranslation } from 'react-i18next';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

const VehicleTypes = {
  Car: 'Car',
  Commercial: 'Commercial Vehicle',
  Motorcycle: 'Motorcycle',
  Motorhome: 'Motorhome'
};

const FuelTypes = {
  Petrol: 'Petrol',
  Diesel: 'Diesel',
  Electric: 'Electric',
  ElectricBattery: 'Electric (Battery)',
  PetrolElectric: 'Petrol/Electric',
  PetrolElectricPlugIn: 'Petrol/Electric Plugin',
  NoLink: 'no link (0010)',
  DieselElectric: 'Diesel/Electric'
};

const VehicleTypeTranslationMapping = {
  [VehicleTypes.Car]: 'vehicle-details.manual-vehicle-details.car',
  [VehicleTypes.Commercial]:
    'vehicle-details.manual-vehicle-details.commercial-vehicle',
  [VehicleTypes.Motorcycle]:
    'vehicle-details.manual-vehicle-details.motorcycle',
  [VehicleTypes.Motorhome]: 'vehicle-details.manual-vehicle-details.motorhome'
};

const FuelTypeTranslationMapping = {
  [FuelTypes.Petrol]: 'vehicle-details.manual-vehicle-details.petrol',
  [FuelTypes.Diesel]: 'vehicle-details.manual-vehicle-details.diesel',
  [FuelTypes.Electric]: 'vehicle-details.manual-vehicle-details.electric',
  [FuelTypes.ElectricBattery]:
    'vehicle-details.manual-vehicle-details.electric-battery',
  [FuelTypes.PetrolElectric]:
    'vehicle-details.manual-vehicle-details.petrol-electric',
  [FuelTypes.PetrolElectricPlugIn]:
    'vehicle-details.manual-vehicle-details.petrol-electric-plugin',
  [FuelTypes.NoLink]: 'vehicle-details.manual-vehicle-details.no-link',
  [FuelTypes.DieselElectric]:
    'vehicle-details.manual-vehicle-details.diesel-electric'
};

type initialQuerySelections = {
  firstRegistrationDate: string | undefined;
  firstRegistrationDateNew: string | undefined;
  vehicleTypeId: string;
  vehicleMakeId: string;
  vehicleModelId: string;
  vehicleFuelTypeId: string;
  vehicleEngineSizeId: string;
  vehicleModelTrimId: string;
  vehicleType: string;
  make: string;
  model: string;
  fuelType: string;
  engineSize: string;
};

export const initialManualQuerySelections: initialQuerySelections = {
  firstRegistrationDate: undefined,
  firstRegistrationDateNew: undefined,
  vehicleTypeId: '',
  vehicleMakeId: '',
  vehicleModelId: '',
  vehicleFuelTypeId: '',
  vehicleEngineSizeId: '',
  vehicleModelTrimId: '',
  vehicleType: '',
  make: '',
  model: '',
  fuelType: '',
  engineSize: ''
};

type VehicleDetailsManualProps = {
  onConfirmManualVehicleModal: (
    manualQuerySelections: ManualQuerySelections,
    enginePowerBHP?: number,
    enginePowerKWH?: number,
    driveTypeId?: number,
    vin?: string,
    modelType?: string,
    fuelTypeCode?: string
  ) => void;
  isFRDOptional?: boolean;
  forTest?: boolean;
  vehicle?: VehicleState;
  culture: string;
  expiryDetails?: VinWarrantyExpiry;
};

type VehicleType = {
  id: string;
  text: string;
  value: string;
  disabled: boolean;
};

const initialVehicleTypes: VehicleType[] = [
  { id: '1', text: 'Car', value: '1', disabled: false },
  { id: '2', text: 'Commercial Vehicle', value: '2', disabled: false },
  { id: '3', text: 'Motorcycle', value: '3', disabled: false },
  { id: '4', text: 'Motorhome', value: '4', disabled: false }
];

const initialVehicleTypesNonUK: VehicleType[] = [
  { id: '1', text: 'PKW', value: '1', disabled: false }
];

const VehicleDetailsManual = ({
  onConfirmManualVehicleModal,
  isFRDOptional,
  forTest,
  vehicle,
  culture,
  expiryDetails
}: VehicleDetailsManualProps): JSX.Element => {
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);
  const userToken = useAppSelector((s) => s.user.token);
  const brandName = useAppSelector((s) => s.brand.brandName);

  const nonGBSite =
    countryCode.toUpperCase() !== CountryIsoCodes.GB.toUpperCase();

  const areFieldsPrepopulated =
    nonGBSite && expiryDetails !== null && brandName === 'landrover';

  const getInitialTypes = () => {
    if (forTest == false) {
      return [];
    } else {
      return nonGBSite ? initialVehicleTypesNonUK : initialVehicleTypes;
    }
  };

  const [vehicleTypes, setVehicleTypes] =
    useState<SelectOpt[]>(getInitialTypes());
  const [vehicleMakes, setVehicleMakes] = useState<SelectOpt[]>([]);
  const [vehicleModels, setVehicleModels] = useState<SelectOpt[]>([]);
  const [vehicleFuelTypes, setVehicleFuelTypes] = useState<SelectOpt[]>([]);
  const [vehicleEngineSizes, setVehicleEngineSizes] = useState<SelectOpt[]>([]);
  const [vehicleModelTrims, setVehicleModelTrims] = useState<SelectOpt[]>([]);
  const [modelTrimLookupData, setModelTrimLookupData] = useState<
    VehicleModelTrimLookup[]
  >([]);

  const [loading, setLoading] = useState(false);
  const [hasZeroCapacityOption, setHasZeroCapacityOption] = useState(false);

  const [isVehicleSetManually, setIsVehicleSetManually] = useState(false);

  const electricFuelTypeId = '4';
  const carType = 1;

  const [manualQuerySelections, setManualQuerySelections] =
    useState<ManualQuerySelections>(initialManualQuerySelections);

  const [enableConfirmation, setEnableConfirmation] = useState(false);

  const { t } = useTranslation();

  const prepopulatedManualQuerySelections = initialManualQuerySelections;

  const isElectric =
    manualQuerySelections.vehicleFuelTypeId === electricFuelTypeId;

  const showNoModelTypesError =
    !loading &&
    areFieldsPrepopulated &&
    vehicleFuelTypes.length > 0 &&
    vehicleEngineSizes.length === 0 &&
    !CommonValidation.isEmpty(manualQuerySelections.vehicleFuelTypeId) &&
    !SelectInputHelper.isNullValue(manualQuerySelections.vehicleFuelTypeId);

  const showVehicleType =
    (!CommonValidation.isDateNull(
      manualQuerySelections.firstRegistrationDate,
      true
    ) &&
      CommonValidation.isNotEmpty(
        manualQuerySelections.firstRegistrationDate
      )) ||
    areFieldsPrepopulated;
  const showVehicleMake =
    (!SelectInputHelper.isNullValue(manualQuerySelections.vehicleTypeId) &&
      !CommonValidation.isEmpty(manualQuerySelections.vehicleTypeId) &&
      vehicleMakes.length > 0) ||
    areFieldsPrepopulated;
  const showVehicleModel =
    (showVehicleMake &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleMakeId) &&
      !CommonValidation.isEmpty(manualQuerySelections.vehicleMakeId)) ||
    areFieldsPrepopulated;
  const showFuelType =
    (showVehicleModel &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleModelId) &&
      !CommonValidation.isEmpty(manualQuerySelections.vehicleModelId)) ||
    areFieldsPrepopulated;
  const showEngineSize =
    showFuelType &&
    !isElectric &&
    !SelectInputHelper.isNullValue(manualQuerySelections.vehicleFuelTypeId) &&
    !CommonValidation.isEmpty(manualQuerySelections.vehicleFuelTypeId) &&
    !hasZeroCapacityOption;
  const showModelType =
    ((showEngineSize || isElectric) &&
      !SelectInputHelper.isNullValue(
        manualQuerySelections.vehicleEngineSizeId
      ) &&
      !CommonValidation.isEmpty(manualQuerySelections.vehicleEngineSizeId)) ||
    (hasZeroCapacityOption && !showNoModelTypesError);

  const isVehicleSet =
    vehicle && !CommonValidation.isEmpty(vehicle.vehicleDetails.make);

  const currentPath = useCurrentPath();

  const [showModal, setShowModal] = useState(
    nonGBSite &&
      !isVehicleSet &&
      currentPath === PageAbsolutePath.VehicleDetails
  );

  const showNoMakesError =
    !loading &&
    vehicleTypes.length > 0 &&
    vehicleMakes.length === 0 &&
    !CommonValidation.isEmpty(manualQuerySelections.vehicleTypeId) &&
    !SelectInputHelper.isNullValue(manualQuerySelections.vehicleTypeId);

  useEffect(() => {
    if (nonGBSite) {
      if (isVehicleSet) {
        const existingManualQuerySelections: ManualQuerySelections = {
          firstRegistrationDate: vehicle.vehicleDeliveryDate,
          firstRegistrationDateNew: vehicle.vehicleDeliveryDate,
          vehicleTypeId: vehicle.vehicleDetails.vehicleTypeId,
          vehicleMakeId: vehicle.vehicleDetails.makeId,
          vehicleModelId: vehicle.vehicleDetails.modelId,
          vehicleFuelTypeId: vehicle.vehicleDetails.fuelTypeId.toString(),
          vehicleEngineSizeId: vehicle.vehicleDetails.engineSize,
          vehicleModelTrimId: vehicle.vehicleDetails.modelTrimId.toString(),
          vehicleType: vehicle.vehicleDetails.vehicleType,
          make: vehicle.vehicleDetails.make,
          model: vehicle.vehicleDetails.model,
          fuelType: vehicle.vehicleDetails.fuelType,
          engineSize: vehicle.vehicleDetails.engineSize
        };

        setManualQuerySelections(existingManualQuerySelections);
      } else {
        const controller = new AbortController();

        prepopulateFields(controller.signal);

        return () => {
          controller.abort();
        };
      }
    }
  }, [expiryDetails]);

  useEffect(() => {
    if (nonGBSite && isVehicleSet && !isVehicleSetManually && showModal) {
      reloadOptions(manualQuerySelections);

      setIsVehicleSetManually(true);
    }
  }, [showModal]);

  useEffect(() => {
    const enable = enableConfirmButton();

    if (enable != enableConfirmation) {
      setEnableConfirmation(enable);
    }
  }, [manualQuerySelections]);

  useEffect(() => {
    const controller = new AbortController();

    if (!vehicleEngineSizes.length && isElectric) {
      populateModelTrim('0', '0', controller.signal);
    }

    return () => {
      controller.abort();
    };
  }, [vehicleEngineSizes]);

  const reloadOptions = (selections: ManualQuerySelections) => {
    const abortController = new AbortController();

    getVehicleTypes(abortController.signal);

    getVehicleMakes(selections.vehicleTypeId, abortController.signal);

    getVehicleModels(selections.vehicleMakeId, abortController.signal);

    getFuelTypes(selections.vehicleModelId, abortController.signal);

    getEngineSizes(selections.vehicleFuelTypeId, abortController.signal);

    getModelTrims(selections.vehicleEngineSizeId, abortController.signal);

    return function cancel() {
      abortController.abort();
    };
  };

  const enableConfirmButton = () => {
    const enable =
      (isFRDOptional ||
        (!CommonValidation.isDateNull(
          manualQuerySelections.firstRegistrationDate,
          true
        ) &&
          CommonValidation.isNotEmpty(
            manualQuerySelections.firstRegistrationDate
          ))) &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleTypeId) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleTypeId) &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleMakeId) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleMakeId) &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleModelId) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleModelId) &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleFuelTypeId) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleFuelTypeId) &&
      !SelectInputHelper.isNullValue(
        manualQuerySelections.vehicleEngineSizeId
      ) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleEngineSizeId) &&
      !SelectInputHelper.isNullValue(
        manualQuerySelections.vehicleModelTrimId
      ) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleModelTrimId);

    return enable;
  };

  const onShowModal = () => {
    if (areFieldsPrepopulated && !isVehicleSet) {
      setPrepopulatedFields();
    }
    setShowModal(true);
  };

  const onCloseModal = () => {
    if (!isVehicleSet) {
      setManualQuerySelections(initialManualQuerySelections);
    }

    setShowModal(false);
  };

  const onConfirm = () => {
    const vehicleModelTrim = modelTrimLookupData.find(
      (m) => m.id == manualQuerySelections.vehicleModelTrimId
    );

    onConfirmManualVehicleModal(
      manualQuerySelections,
      vehicleModelTrim?.enginePowerBHP,
      vehicleModelTrim?.enginePowerKWH,
      vehicleModelTrim?.driveTypeId,
      vehicleModelTrim?.vin ?? '',
      vehicleModelTrim?.text,
      vehicleModelTrim?.fuelTypeCode
    );

    setIsVehicleSetManually(true);

    setShowModal(false);
  };

  const onDateCalendarClose = () => {
    const abortController = new AbortController();

    if (
      manualQuerySelections.firstRegistrationDateNew !=
      manualQuerySelections.firstRegistrationDate
    ) {
      if (areFieldsPrepopulated) {
        const newManualQuerySelections = {
          ...manualQuerySelections,
          ['firstRegistrationDate']:
            manualQuerySelections.firstRegistrationDateNew
        };

        setManualQuerySelections(newManualQuerySelections);
      } else {
        const newManualQuerySelections = {
          ...manualQuerySelections,
          ['firstRegistrationDate']:
            manualQuerySelections.firstRegistrationDateNew,
          ['vehicleTypeId']: SelectInputHelper.NullValue(),
          ['vehicleMakeId']: SelectInputHelper.NullValue(),
          ['vehicleModelId']: SelectInputHelper.NullValue(),
          ['vehicleFuelTypeId']: SelectInputHelper.NullValue(),
          ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
          ['vehicleModelTrimId']: SelectInputHelper.NullValue()
        };

        setManualQuerySelections(newManualQuerySelections);
      }

      if (
        !CommonValidation.isDateNull(
          manualQuerySelections.firstRegistrationDateNew,
          true
        ) &&
        CommonValidation.isNotEmpty(
          manualQuerySelections.firstRegistrationDateNew
        ) &&
        !areFieldsPrepopulated
      ) {
        getVehicleTypes(abortController.signal);
      }
    }

    return function cancel() {
      abortController.abort();
    };
  };

  const prepopulateFields = (signal: AbortSignal) => {
    getVehicleTypes(signal);

    getVehicleMakes('1', signal);
  };

  const setPrepopulatedFields = () => {
    const newManualQuerySelections = {
      ...manualQuerySelections,
      ['vehicleTypeId']: vehicleTypes[0].value ?? SelectInputHelper.NullValue(),
      ['vehicleMakeId']: vehicleMakes[0].value ?? SelectInputHelper.NullValue(),
      ['vehicleModelId']:
        vehicleModels[0].value ?? SelectInputHelper.NullValue(),
      ['vehicleFuelTypeId']: SelectInputHelper.NullValue(),
      ['vehicleEngineSizeId']: hasZeroCapacityOption
        ? vehicleEngineSizes[0].value
        : SelectInputHelper.NullValue(),
      ['vehicleModelTrimId']: SelectInputHelper.NullValue()
    };

    setManualQuerySelections(newManualQuerySelections);
  };

  const getVehicleTypes = (signal: AbortSignal) => {
    new VehicleApi().getVehicleTypes(
      countryCode,
      getRegistrationDate(),
      userToken,
      successCallbackVehicleTypes,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getVehicleMakes = (value: string, signal: AbortSignal) => {
    setLoading(true);
    new VehicleApi().getVehicleMakes(
      getRegistrationDate(),
      value,
      userToken,
      successCallbackVehicleMakes,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getVehicleModels = (
    value: string,
    signal: AbortSignal,
    vehicleTypeId?: string
  ) => {
    new VehicleApi().getVehicleModels(
      getRegistrationDate(),
      vehicleTypeId ?? manualQuerySelections.vehicleTypeId,
      value,
      userToken,
      successCallbackVehicleModels,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getFuelTypes = (
    value: string,
    signal: AbortSignal,
    vehicleTypeId?: string,
    vehicleMakeId?: string
  ) => {
    new VehicleApi().getFuelTypes(
      countryCode,
      getRegistrationDate(),
      vehicleTypeId ?? manualQuerySelections.vehicleTypeId,
      vehicleMakeId ?? manualQuerySelections.vehicleMakeId,
      value,
      userToken,
      successCallbackFuelTypes,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getEngineSizes = (value: string, signal: AbortSignal) => {
    new VehicleApi().getEngineSizes(
      getRegistrationDate(),
      manualQuerySelections.vehicleTypeId,
      manualQuerySelections.vehicleMakeId,
      manualQuerySelections.vehicleModelId,
      value,
      userToken,
      successCallbackEngineSizes,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getModelTrims = (
    value: string,
    signal: AbortSignal,
    vehicleFuelTypeId?: string
  ) => {
    new VehicleApi().getModelTrims(
      getRegistrationDate(),
      manualQuerySelections.vehicleTypeId,
      manualQuerySelections.vehicleMakeId,
      manualQuerySelections.vehicleModelId,
      vehicleFuelTypeId ?? manualQuerySelections.vehicleFuelTypeId,
      value,
      userToken,
      successCallbackModelTrims,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const populateModelTrim = (
    engineSizeId: string,
    engineSize: string,
    abortSignal: AbortSignal
  ) => {
    if (
      !SelectInputHelper.isNullValue(engineSizeId) &&
      engineSizeId != manualQuerySelections.vehicleEngineSizeId
    ) {
      getModelTrims(engineSizeId, abortSignal);
    }

    const newManualQuerySelections = {
      ...manualQuerySelections,
      ['engineSize']: engineSize,
      ['vehicleEngineSizeId']: engineSizeId,
      ['vehicleModelTrimId']: SelectInputHelper.NullValue()
    };
    setManualQuerySelections(newManualQuerySelections);
  };

  const onChangeDetailWithLookup = (
    selectedValue: string,
    lookupValue: number,
    options?: SelectOpt[]
  ) => {
    let text = '';
    if (options) {
      text =
        options.find((o) => o.value.toString() === selectedValue)?.text ?? '';
    }
    const abortController = new AbortController();

    switch (lookupValue) {
      case VehicleLookup.Types:
        {
          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['firstRegistrationDateNew']: selectedValue
          };

          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      case VehicleLookup.Makes:
        {
          if (
            !SelectInputHelper.isNullValue(selectedValue) &&
            selectedValue != manualQuerySelections.vehicleTypeId
          ) {
            getVehicleMakes(selectedValue, abortController.signal);
          }

          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['vehicleType']: text,
            ['vehicleTypeId']: selectedValue,
            ['vehicleMakeId']: SelectInputHelper.NullValue(),
            ['vehicleModelId']: SelectInputHelper.NullValue(),
            ['vehicleFuelTypeId']: SelectInputHelper.NullValue(),
            ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
            ['vehicleModelTrimId']: SelectInputHelper.NullValue()
          };
          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      case VehicleLookup.Model:
        {
          if (
            !SelectInputHelper.isNullValue(selectedValue) &&
            selectedValue != manualQuerySelections.vehicleMakeId
          ) {
            getVehicleModels(selectedValue, abortController.signal);
          }
          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['make']: text,
            ['vehicleMakeId']: selectedValue,
            ['vehicleModelId']: SelectInputHelper.NullValue(),
            ['vehicleFuelTypeId']: SelectInputHelper.NullValue(),
            ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
            ['vehicleModelTrimId']: SelectInputHelper.NullValue()
          };
          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      case VehicleLookup.Fueltype:
        {
          if (
            !SelectInputHelper.isNullValue(selectedValue) &&
            selectedValue != manualQuerySelections.vehicleModelId
          ) {
            getFuelTypes(selectedValue, abortController.signal);
          }
          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['model']: text,
            ['vehicleModelId']: selectedValue,
            ['vehicleFuelTypeId']: SelectInputHelper.NullValue(),
            ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
            ['vehicleModelTrimId']: SelectInputHelper.NullValue()
          };
          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      case VehicleLookup.Enginesize:
        {
          if (
            !SelectInputHelper.isNullValue(selectedValue) &&
            selectedValue != manualQuerySelections.vehicleFuelTypeId
          ) {
            if (selectedValue === electricFuelTypeId) {
              setVehicleEngineSizes([]);
            } else {
              getEngineSizes(selectedValue, abortController.signal);
            }
          }

          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['fuelType']: text,
            ['vehicleFuelTypeId']: selectedValue,
            ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
            ['vehicleModelTrimId']: SelectInputHelper.NullValue()
          };
          setManualQuerySelections(newManualQuerySelections);

          if (areFieldsPrepopulated) {
            prepopulatedManualQuerySelections.vehicleFuelTypeId = selectedValue;
            prepopulatedManualQuerySelections.fuelType = text;
          }
        }
        break;
      case VehicleLookup.Modeltrim:
        populateModelTrim(selectedValue, text, abortController.signal);
        break;
      case VehicleLookup.Finished:
        {
          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['vehicleModelTrimId']: selectedValue
          };
          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      default:
        break;
    }

    return () => {
      abortController.abort();
    };
  };

  const successCallbackVehicleTypes = (data: VehicleInfoLookup[]) => {
    const translatedData = translateVehicleTypes(data);

    const dataToMap = areFieldsPrepopulated
      ? translatedData.filter((d) => Number(d.id) === carType)
      : translatedData;

    const mappedVehicleTypes = mapVehicleSection(dataToMap);

    setVehicleTypes(mappedVehicleTypes);

    if (areFieldsPrepopulated) {
      const newManualQuerySelections = {
        ...manualQuerySelections,
        vehicleTypeId: mappedVehicleTypes[0].value,
        vehicleType: mappedVehicleTypes[0].text
      };

      prepopulatedManualQuerySelections.vehicleTypeId =
        mappedVehicleTypes[0].value;

      setManualQuerySelections(newManualQuerySelections);
    }
  };

  const successCallbackVehicleMakes = (data: VehicleInfoLookup[]) => {
    const mappedVehicleMakes = mapVehicleSection(
      brandName === 'landrover'
        ? data.filter((d) => d.text === 'Land Rover')
        : data
    );
    setLoading(false);
    setVehicleMakes(mappedVehicleMakes);

    if (areFieldsPrepopulated) {
      const newManualQuerySelections = {
        ...manualQuerySelections,
        vehicleMakeId: mappedVehicleMakes[0].value,
        make: mappedVehicleMakes[0].text
      };

      setManualQuerySelections(newManualQuerySelections);

      prepopulatedManualQuerySelections.vehicleMakeId =
        mappedVehicleMakes[0].value;
      prepopulatedManualQuerySelections.make = mappedVehicleMakes[0].text;

      if (areFieldsPrepopulated && mappedVehicleMakes.length > 0) {
        const abortController = new AbortController();

        getVehicleModels(
          prepopulatedManualQuerySelections.vehicleMakeId,
          abortController.signal,
          carType.toString()
        );

        return () => {
          abortController.abort();
        };
      }
    }
  };

  const successCallbackVehicleModels = (data: VehicleInfoLookup[]) => {
    const model = expiryDetails?.modelDescription?.split('-')[0].trim() ?? '';

    const mappedVehicleModels = mapVehicleSection(
      areFieldsPrepopulated ? data.filter((d) => d.text === model) : data
    );
    setVehicleModels(mappedVehicleModels);

    if (areFieldsPrepopulated && mappedVehicleModels.length > 0) {
      const newManualQuerySelections = {
        ...manualQuerySelections,
        vehicleModelId: mappedVehicleModels[0].value
      };

      setManualQuerySelections(newManualQuerySelections);

      prepopulatedManualQuerySelections.vehicleModelId =
        mappedVehicleModels[0].value;
      prepopulatedManualQuerySelections.model = mappedVehicleModels[0].text;

      if (mappedVehicleModels.length > 0) {
        const abortController = new AbortController();

        getFuelTypes(
          prepopulatedManualQuerySelections.vehicleModelId,
          abortController.signal,
          prepopulatedManualQuerySelections.vehicleTypeId,
          prepopulatedManualQuerySelections.vehicleMakeId
        );

        return () => {
          abortController.abort();
        };
      }
    }
  };

  const successCallbackFuelTypes = (data: VehicleInfoLookup[]) => {
    const translatedData = translateFuelTypes(data);
    const mappedFuelTypes = mapVehicleSection(translatedData);
    setVehicleFuelTypes(mappedFuelTypes);
  };

  const successCallbackEngineSizes = (data: VehicleInfoLookup[]) => {
    setHasZeroCapacityOption(areFieldsPrepopulated);

    const engineSizeData = areFieldsPrepopulated
      ? data.filter((d) => d.text === '0')
      : data;

    const mappedEngineSizes = mapVehicleSection(engineSizeData);
    setVehicleEngineSizes(mappedEngineSizes);

    const loadNextDropdown =
      areFieldsPrepopulated && mappedEngineSizes.length > 0;

    if (loadNextDropdown) {
      prepopulatedManualQuerySelections.vehicleEngineSizeId =
        mappedEngineSizes[0].value;
      prepopulatedManualQuerySelections.engineSize = mappedEngineSizes[0].text;

      const newManualQuerySelections = {
        ...manualQuerySelections,
        vehicleEngineSizeId: mappedEngineSizes[0].value,
        vehicleFuelTypeId: prepopulatedManualQuerySelections.vehicleFuelTypeId,
        fuelType: prepopulatedManualQuerySelections.fuelType,
        vehicleMakeId: prepopulatedManualQuerySelections.vehicleMakeId,
        make: prepopulatedManualQuerySelections.make,
        vehicleModelId: prepopulatedManualQuerySelections.vehicleModelId,
        model: prepopulatedManualQuerySelections.model,
        engineSize: prepopulatedManualQuerySelections.engineSize
      };

      setManualQuerySelections(newManualQuerySelections);

      const abortController = new AbortController();

      getModelTrims(
        mappedEngineSizes[0].value,
        abortController.signal,
        prepopulatedManualQuerySelections.vehicleFuelTypeId
      );

      return () => {
        abortController.abort();
      };
    }
  };

  const successCallbackModelTrims = (data: VehicleModelTrimLookup[]) => {
    setModelTrimLookupData(data);
    const mappedModelTrims = mapVehicleSection(data);

    setVehicleModelTrims(mappedModelTrims);

    if (areFieldsPrepopulated) {
      const newManualQuerySelections = {
        ...manualQuerySelections,
        vehicleFuelTypeId: prepopulatedManualQuerySelections.vehicleFuelTypeId,
        fuelType: prepopulatedManualQuerySelections.fuelType,
        vehicleMakeId: prepopulatedManualQuerySelections.vehicleMakeId,
        make: prepopulatedManualQuerySelections.make,
        vehicleModelId: prepopulatedManualQuerySelections.vehicleModelId,
        model: prepopulatedManualQuerySelections.model,
        engineSize: prepopulatedManualQuerySelections.engineSize,
        vehicleEngineSizeId:
          prepopulatedManualQuerySelections.vehicleEngineSizeId,
        vehicleModelTrimId: mappedModelTrims[0].value
      };

      setManualQuerySelections(newManualQuerySelections);
    }
  };

  const failureCallbackVehicleInfo = (error: string) => {
    console.log('Failed to get with: ', error);
    setLoading(false);
  };

  const mapVehicleSection = (
    vehicleInformation: VehicleInfoLookup[]
  ): SelectOpt[] => {
    const mappedVehicleOptions: SelectOpt[] = [];

    vehicleInformation.forEach((info) => {
      const option = {
        id: info.id,
        text: info.text,
        value: info.value,
        disabled: false
      };
      mappedVehicleOptions.push(option);
    });
    return mappedVehicleOptions;
  };

  const activeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showModal && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [
    showVehicleType,
    showVehicleMake,
    showVehicleModel,
    showFuelType,
    showEngineSize,
    showModelType
  ]);

  const getRegistrationDate = (): string => {
    if (
      !CommonValidation.isDateNull(
        manualQuerySelections.firstRegistrationDateNew,
        true
      ) &&
      CommonValidation.isNotEmpty(
        manualQuerySelections.firstRegistrationDateNew
      )
    ) {
      return getDateString(manualQuerySelections.firstRegistrationDateNew);
    } else if (isFRDOptional === true || areFieldsPrepopulated) {
      const cdh = new ClientDateHandler();

      cdh.setDateValue(false, new Date());

      const todayDateJson = cdh.getJSON();
      return getDateString(todayDateJson);
    } else if (
      vehicle &&
      !CommonValidation.isDateNull(vehicle.vehicleDeliveryDate, true)
    ) {
      return getDateString(vehicle.vehicleDeliveryDate);
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (isFRDOptional === true) {
      const abortController = new AbortController();

      new VehicleApi().getVehicleTypes(
        countryCode,
        getRegistrationDate(),
        userToken,
        successCallbackVehicleTypes,
        failureCallbackVehicleInfo,
        abortController.signal
      );
    }
  }, [isFRDOptional]);

  const getFRDFieldLabel = () => {
    let label = t(
      'vehicle-details.manual-vehicle-details.vehicle-registration-date-label'
    );

    if (isFRDOptional) {
      label += ` ${t('vehicle-details.optional-field-text')}`;
    }

    return label;
  };

  const yesterday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  const labelText = nonGBSite ? '' : t('vehicle-details.show-modal-button');

  const buttonText = nonGBSite
    ? t('vehicle-details.edit-vehicle-details-button')
    : t('vehicle-details.enter-details-manually-button');

  const translateVehicleTypes = (
    vehicleTypesToTranslate: VehicleInfoLookup[]
  ): VehicleInfoLookup[] => {
    return vehicleTypesToTranslate.map((vehicleType) => {
      const translationKey = VehicleTypeTranslationMapping[vehicleType.text];

      let text = vehicleType.text;

      if (translationKey) {
        text = t(translationKey, { defaultValue: vehicleType.text });
      }

      return {
        ...vehicleType,
        text: text
      };
    });
  };

  const translateFuelTypes = (
    fuelTypesToTranslate: VehicleInfoLookup[]
  ): VehicleInfoLookup[] => {
    return fuelTypesToTranslate.map((fuelType) => {
      const translationKey = FuelTypeTranslationMapping[fuelType.text];

      let text = fuelType.text;

      if (translationKey) {
        text = t(translationKey, { defaultValue: fuelType.text });
      }

      return {
        ...fuelType,
        text
      };
    });
  };

  //const translateVehicleType = (
  //  vehicleType: VehicleInfoLookup
  //): VehicleInfoLookup => {
  //  const translationKey = VehicleTypeTranslationMapping[vehicleType.text];

  //  let text = vehicleType.text;

  //  if (translationKey) {
  //    text = t(translationKey, { defaultValue: vehicleType.text });
  //  }

  //  return {
  //    ...vehicleType,
  //    text: text
  //  };
  //};

  //const translateFuelType = (
  //  fuelType: VehicleInfoLookup
  //): VehicleInfoLookup => {
  //  const translationKey = FuelTypeTranslationMapping[fuelType.text];

  //  let text = fuelType.text;

  //  if (translationKey) {
  //    text = t(translationKey, { defaultValue: fuelType.text });
  //  }

  //  return {
  //    ...fuelType,
  //    text
  //  };
  //};

  return (
    <>
      <div className='flex-row m2-top m1-bottom'>
        <div>
          {!isFRDOptional && <>{labelText}</>}

          <ButtonCommand
            name='btn-manual-vehicle'
            text={buttonText}
            onClick={onShowModal}
            additionalClassNames='bold'
          />
        </div>
      </div>

      <Modal isOpen={showModal}>
        <ModalHeader
          toggle={onCloseModal}
          close={
            <ButtonIcon
              icon={faXmark}
              size='xs'
              className='close'
              onClick={onCloseModal}
              ariaLabel={t('button-text-close')}
            />
          }
          className='modal-header-title cms-modal-header-title justify-content-between'>
          {t(
            'vehicle-details.manual-vehicle-details.manual-vehicle-details-header'
          )}
        </ModalHeader>
        <ModalBody>
          <>
            <div className='flex-row'>
              <div className='control'>
                <DateInput
                  name='first-registration-date'
                  className='input-text w250'
                  haslabel
                  label={getFRDFieldLabel()}
                  placeholder={t(
                    'vehicle-details.manual-vehicle-details.vehicle-registration-date-placeholder'
                  )}
                  forceTimeReset
                  useJson
                  error=''
                  value={manualQuerySelections.firstRegistrationDate}
                  maximumDate={nonGBSite ? yesterday : undefined}
                  onDateChanged={(e: React.FormEvent<HTMLInputElement>) => {
                    onChangeDetailWithLookup(
                      (e.target as HTMLSelectElement).value,
                      VehicleLookup.Types
                    );
                  }}
                  onCalendarClose={onDateCalendarClose}
                  culture={culture}
                />
              </div>
            </div>
            <div className='flex-row'>
              {(isFRDOptional || showVehicleType) && (
                <div className='inline-control'>
                  <label className='control-label' htmlFor='vehicle_type'>
                    {t(
                      'vehicle-details.manual-vehicle-details.vehicle-type-label'
                    )}
                  </label>
                  <div className='control'>
                    <SelectInput
                      name='vehicle_type'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleTypeId}
                      options={vehicleTypes}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Makes,
                          vehicleTypes
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.vehicle-type-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              <div id='vehicle-id-scroll' ref={activeRef} />
              {showVehicleMake && (
                <div className='inline-control'>
                  <label className='control-label' htmlFor='vehicle_make'>
                    {t(
                      'vehicle-details.manual-vehicle-details.vehicle-make-label'
                    )}
                  </label>
                  <div className='control'>
                    <SelectInput
                      name='vehicle_make'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleMakeId}
                      options={vehicleMakes}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Model,
                          vehicleMakes
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.vehicle-make-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              {showNoMakesError && (
                <div className='flex-row'>
                  <div className='control-label'>
                    {t('vehicle-details.manual-vehicle-details.no-makes-found')}
                  </div>
                </div>
              )}
              <div id='vehicle-make-scroll' ref={activeRef} />
            </div>
            <div className='flex-row'>
              {showVehicleModel && (
                <div className='inline-control'>
                  <label className='control-label' htmlFor='vehicle-model'>
                    {t(
                      'vehicle-details.manual-vehicle-details.vehicle-model-label'
                    )}
                  </label>
                  <div className='control'>
                    <SelectInput
                      name='vehicle-model'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleModelId}
                      options={vehicleModels}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Fueltype,
                          vehicleModels
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.vehicle-model-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              <div id='vehicle-model-scroll' ref={activeRef} />
              {showFuelType && (
                <div className='inline-control'>
                  <label className='control-label' htmlFor='fuel-type'>
                    {t('vehicle-details.fuel-type-label')}
                  </label>
                  <div className='control' ref={activeRef}>
                    <SelectInput
                      name='fuel-type'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleFuelTypeId}
                      options={vehicleFuelTypes}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Enginesize,
                          vehicleFuelTypes
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.fuel-type-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              <div id='fuel-type-scroll' ref={activeRef} />
            </div>
            <div className='flex-row'>
              {showEngineSize && (
                <div className='inline-control'>
                  <label className='control-label' htmlFor='engine-size'>
                    {t(
                      'vehicle-details.manual-vehicle-details.engine-size-label'
                    )}
                  </label>
                  <div className='control'>
                    <SelectInput
                      name='engine-size'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleEngineSizeId}
                      options={vehicleEngineSizes}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Modeltrim,
                          vehicleEngineSizes
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.engine-size-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              <div id='engine-size-scroll' ref={activeRef} />
              {showModelType && (
                <div className='inline-control'>
                  <label className='control-label' htmlFor='model-type'>
                    {t('vehicle-details.model-type-label')}
                  </label>
                  <div className='control'>
                    <SelectInput
                      name='model-type'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleModelTrimId}
                      options={vehicleModelTrims}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Finished,
                          vehicleModelTrims
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.model-type-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              {showNoModelTypesError && (
                <div className='flex-row'>
                  <div className='control-label'>
                    {t(
                      'vehicle-details.manual-vehicle-details.no-model-types-found'
                    )}
                  </div>
                </div>
              )}
              <div id='model-type-scroll' ref={activeRef} />
            </div>
          </>
        </ModalBody>
        <ModalFooter>
          <div className='flex-row button-row'>
            <ButtonCommand
              name='btn-close-manual-vehicle'
              text={t('vehicle-details.button-cancel-text')}
              onClick={onCloseModal}
            />

            <ButtonConfirm
              name='btn-confirm-manual-vehicle'
              text={t('vehicle-details.button-confirm-text')}
              additionalClassNames='m05-left'
              onClick={onConfirm}
              disabled={!enableConfirmation}
            />
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default VehicleDetailsManual;
